import { Signal } from 'typed-signals';
import { Container, Point } from 'pixi.js';
import last from 'lodash/last';
import { LevelTaskType } from '../tasks/types';
import type { LevelTasksTracker } from '../tasks/LevelTasksTracker';
import type { BoardView } from '../boardMVC/views/board/BoardView';
import type { TileModel } from '../boardMVC/models/board/TileModel';
import type { BlockerModel } from '../boardMVC/types';
import type { TaskScoreBubbleTextColors } from './TaskScoreBubbleView';
import { TaskScoreBubbleView } from './TaskScoreBubbleView';
import { GOAL_SCORE_COLORS, SCORE_COLOR_MAP } from './constants';
import type { EventScheduler } from '../../utils/EventScheduler';
import { TaskScoreBubbleAnimator } from './TaskScoreBubbleAnimator';
import { soundManager } from '../../app/SoundManager';
import { BirdTaskBubbleView } from './BirdTaskBubbleView';
import { StarTaskBubbleView } from './StarTaskBubbleView';

interface TasksScoreOverlayDependencies {
  tasksTracker: LevelTasksTracker;
  getGlobalTargetPosition: (taskType: LevelTaskType) => Point;
  scheduler: EventScheduler;
}

interface TaskScoreBubbleConfig {
  colors: TaskScoreBubbleTextColors;
  points: number;
  position: Point;
}

export interface CustomTaskBubble extends Container {
  animateTo(position: Point, onComplete: () => void): void;
}

const customTaskBubbles: Partial<Record<LevelTaskType, (bubbleAnimator: TaskScoreBubbleAnimator) => CustomTaskBubble>> =
  {
    [LevelTaskType.Birds]: () => new BirdTaskBubbleView(),
    [LevelTaskType.Stars]: (animator) => new StarTaskBubbleView(animator),
  };

export class TaskScoreOverlay extends Container {
  private boardView: BoardView | null = null;

  public readonly onTaskViewHit = new Signal<(points: number, taskType: LevelTaskType) => void>();

  public readonly onFinalParticleAnimation = new Signal();

  private readonly activeBubbles = new Set<TaskScoreBubbleView>();

  private readonly bubbleAnimator: TaskScoreBubbleAnimator;

  constructor(private readonly dependencies: TasksScoreOverlayDependencies) {
    super();
    this.bubbleAnimator = new TaskScoreBubbleAnimator(dependencies.scheduler);
    this.bubbleAnimator.startWinParticles.connect(() => {
      this.onFinalParticleAnimation.emit();
    });
  }

  public setBoardView(boardView: BoardView): void {
    this.boardView = boardView;
  }

  private getScoreBubbleConfig(tilesOrBlocker: BlockerModel | TileModel[]): TaskScoreBubbleConfig {
    const position = new Point(0, 0);
    let colors = GOAL_SCORE_COLORS;
    let points = 1;

    if (Array.isArray(tilesOrBlocker)) {
      const lastTile = last(tilesOrBlocker);
      if (lastTile) {
        this.boardView?.getTileView(lastTile.id)?.getGlobalPosition(position);
        colors = SCORE_COLOR_MAP[lastTile.color];
        points = tilesOrBlocker.length;
      }
    } else {
      this.boardView?.getBlockerView(tilesOrBlocker.id)?.getGlobalPosition(position);
    }

    return { position, colors, points };
  }

  private createScoreBubble(bubbleConfig: TaskScoreBubbleConfig, final: boolean): TaskScoreBubbleView | null {
    if (!this.boardView) return null;

    const { colors, points, position } = bubbleConfig;
    const bubble = TaskScoreBubbleView.getFreeInstance().init(colors, points, final);

    bubble.props({
      parent: this,
      position,
    });
    this.activeBubbles.add(bubble);

    return bubble;
  }

  public async animateProgress(tilesOrBlocker: BlockerModel | TileModel[]): Promise<void> {
    if (!this.boardView) return;

    const {
      dependencies: { getGlobalTargetPosition, tasksTracker },
    } = this;

    const taskType = tasksTracker.getItemTaskType(tilesOrBlocker);
    if (!taskType) return;

    const target = getGlobalTargetPosition(taskType);
    const customTaskBubble = customTaskBubbles[taskType]?.(this.bubbleAnimator);

    const final = tasksTracker.allTasksComplete;

    if (!customTaskBubble) {
      // Standard bubble with score
      const bubbleConfig = this.getScoreBubbleConfig(tilesOrBlocker);
      const bubble = this.createScoreBubble(bubbleConfig, final);
      if (!bubble) return;

      await (final
        ? this.bubbleAnimator.animateFinalBubble(bubble, target)
        : this.bubbleAnimator.animateToGoal(bubble, target));

      soundManager.play('Gameplay_Score_Added_AtTopBar');
      this.onTaskViewHit.emit(bubbleConfig.points, taskType);
      this.removeBubble(bubble);
    } else if (!Array.isArray(tilesOrBlocker)) {
      // Custom Sprite
      const position = new Point();
      this.boardView?.getBlockerView(tilesOrBlocker.id)?.getGlobalPosition(position);

      customTaskBubble.props({
        parent: this,
        position,
      });

      customTaskBubble.animateTo(target, () => {
        this.onTaskViewHit.emit(1, taskType);
        customTaskBubble.destroy();
        soundManager.play('Gameplay_Score_Added_AtTopBar');
      });
    }
  }

  private removeBubble(bubble: TaskScoreBubbleView): void {
    if (!this.activeBubbles.has(bubble)) return;
    this.activeBubbles.delete(bubble);
    TaskScoreBubbleView.returnInstance(bubble);
  }

  public destroy(): void {
    this.activeBubbles.forEach((bubble) => {
      TaskScoreBubbleView.returnInstance(bubble);
    });
    this.activeBubbles.clear();
    super.destroy({ children: true });
  }
}
